import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';

interface FileUploadStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  receiptId?: string;
}

interface EmailAddress {
  emailAddressId: string;
  emailAddress: string;
  isActive: boolean;
  createdAt: number;
  deactivatedAt?: number;
}

interface McpConnector {
  connectorId: string;
  clientId: string;
  clientName: string;
  redirectUris: string[];
  endpoint: string;
  createdAt: number;
  lastUsedAt?: number;
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [anonymousSharingEnabled, setAnonymousSharingEnabled] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileUploadStatus[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState<EmailAddress[]>([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);
  const [isCreatingAddress, setIsCreatingAddress] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [mcpConnectors, setMcpConnectors] = useState<McpConnector[]>([]);
  const [mcpLoading, setMcpLoading] = useState(true);
  const [mcpCreating, setMcpCreating] = useState(false);
  const [newConnectorToken, setNewConnectorToken] = useState<string | null>(null);
  const [newConnectorEndpoint, setNewConnectorEndpoint] = useState<string | null>(null);
  const [newConnectorName, setNewConnectorName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  /** Token shown after a key reset – keyed by connectorId */
  const [resetToken, setResetToken] = useState<Record<string, string>>({});
  /** ConnectorId currently being renamed (inline edit) */
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  /** ConnectorId whose key is currently being reset */
  const [resettingKeyId, setResettingKeyId] = useState<string | null>(null);
  /** Guard to prevent double-fire of create */
  const createInFlight = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load email addresses and MCP connectors on mount
  useEffect(() => {
    loadEmailAddresses();
    loadMcpConnectors();
  }, []);

  const loadMcpConnectors = useCallback(async () => {
    try {
      setMcpLoading(true);
      const response = await apiClient.get('/api/mcp/connectors');
      setMcpConnectors(response.data.connectors || []);
    } catch (error) {
      console.error('Failed to load MCP connectors:', error);
    } finally {
      setMcpLoading(false);
    }
  }, []);

  const handleCreateMcpConnector = async () => {
    // Guard against double-fire (React StrictMode or rapid clicks)
    if (createInFlight.current) return;
    createInFlight.current = true;

    const name = newConnectorName.trim() || 'MCP Connector';

    try {
      setMcpCreating(true);
      setNewConnectorToken(null);
      setNewConnectorEndpoint(null);
      const response = await apiClient.post('/api/mcp/connectors', {
        clientName: name,
        redirectUris: ['http://localhost', 'cursor://mcp/callback'],
      });
      setNewConnectorToken(response.data.accessToken);
      setNewConnectorEndpoint(response.data.endpoint || null);
      setShowCreateForm(false);
      setNewConnectorName('');
      await loadMcpConnectors();
    } catch (error: any) {
      const msg = error.response?.data?.error || error.message || 'Failed to create connector';
      alert(msg);
    } finally {
      setMcpCreating(false);
      createInFlight.current = false;
    }
  };

  const handleRevokeMcpConnector = async (connectorId: string) => {
    if (!confirm('Revoke this connector? It will no longer be able to access your data.')) return;
    try {
      await apiClient.delete(`/api/mcp/connectors/${connectorId}`);
      setMcpConnectors((prev) => prev.filter((c) => c.connectorId !== connectorId));
      if (newConnectorToken) setNewConnectorToken(null);
      setResetToken((prev) => { const n = { ...prev }; delete n[connectorId]; return n; });
    } catch (error: any) {
      alert(error.response?.data?.error || error.message || 'Failed to revoke');
    }
  };

  const handleResetKey = async (connectorId: string) => {
    if (resettingKeyId === connectorId) return;
    if (!confirm('Reset the access token? The old token will stop working immediately.')) return;
    try {
      setResettingKeyId(connectorId);
      const response = await apiClient.post(`/api/mcp/connectors/${connectorId}/reset-key`);
      setResetToken((prev) => ({ ...prev, [connectorId]: response.data.accessToken }));
      setNewConnectorToken(null); // Clear creation token so UI cannot show now-invalid old token
    } catch (error: any) {
      alert(error.response?.data?.error || error.message || 'Failed to reset key');
    } finally {
      setResettingKeyId(null);
    }
  };

  const handleRename = async (connectorId: string) => {
    const name = renameValue.trim();
    if (!name) { setRenamingId(null); return; }
    try {
      await apiClient.patch(`/api/mcp/connectors/${connectorId}`, { clientName: name });
      setMcpConnectors((prev) =>
        prev.map((c) => (c.connectorId === connectorId ? { ...c, clientName: name } : c)),
      );
    } catch (error: any) {
      alert(error.response?.data?.error || error.message || 'Failed to rename');
    } finally {
      setRenamingId(null);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Copied to clipboard');
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard. Please select and copy manually.');
    }
  };

  const loadEmailAddresses = async () => {
    try {
      setIsLoadingAddresses(true);
      const response = await apiClient.get('/api/email-addresses');
      setEmailAddresses(response.data.addresses || []);
    } catch (error) {
      console.error('Failed to load email addresses:', error);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnonymousSharingEnabled(e.target.checked);
    // TODO: Call API to update settings
  };

  const handleCreateAddress = async () => {
    try {
      setIsCreatingAddress(true);
      const response = await apiClient.post('/api/email-addresses');
      setEmailAddresses(prev => [...prev, response.data]);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create address';
      alert(errorMessage);
    } finally {
      setIsCreatingAddress(false);
    }
  };

  const handleDeactivateAddress = async (emailAddressId: string) => {
    if (!confirm('Are you sure you want to deactivate this email address? It will no longer receive forwarded receipts.')) {
      return;
    }

    try {
      await apiClient.post(`/api/email-addresses/${emailAddressId}/deactivate`);
      await loadEmailAddresses(); // Reload to get updated status
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to deactivate address';
      alert(errorMessage);
    }
  };

  const handleDeleteAddress = async (emailAddressId: string) => {
    if (!confirm('Are you sure you want to permanently delete this email address? This action cannot be undone.')) {
      return;
    }

    try {
      await apiClient.delete(`/api/email-addresses/${emailAddressId}`);
      setEmailAddresses(prev => prev.filter(a => a.emailAddressId !== emailAddressId));
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete address';
      alert(errorMessage);
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const pdfFiles = Array.from(files).filter(file => 
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );

    if (pdfFiles.length === 0) {
      alert('Please select PDF files only.');
      return;
    }

    const newFiles: FileUploadStatus[] = pdfFiles.map(file => ({
      file,
      status: 'pending' as const,
    }));

    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input to allow selecting same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const uploadFile = async (fileStatus: FileUploadStatus, index: number): Promise<void> => {
    const formData = new FormData();
    formData.append('file', fileStatus.file);

    setSelectedFiles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], status: 'uploading' };
      return updated;
    });

    try {
      const response = await apiClient.post('/api/pdf-upload', formData);

      setSelectedFiles(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          status: 'success',
          receiptId: response.data.receiptId,
        };
        return updated;
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Upload failed';
      setSelectedFiles(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          status: 'error',
          error: errorMessage,
        };
        return updated;
      });
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || isUploading) return;

    setIsUploading(true);
    const pendingFiles = selectedFiles
      .map((file, index) => ({ file, index }))
      .filter(({ file }) => file.status === 'pending');

    // Upload files sequentially
    for (const { file, index } of pendingFiles) {
      await uploadFile(file, index);
    }

    setIsUploading(false);
  };

  const clearCompleted = () => {
    setSelectedFiles(prev => prev.filter(f => f.status === 'pending' || f.status === 'uploading'));
  };

  const handleDeleteAccount = async () => {
    const confirmed = confirm(
      'Are you sure you want to permanently delete your account?\n\n' +
      'This will:\n' +
      '• Delete all your receipts and line items\n' +
      '• Delete all your email forwarding addresses\n' +
      '• Delete all your personal data\n' +
      '• Delete your Cognito user account\n\n' +
      'Note: Anonymous data cannot be removed as it cannot be tied back to you.\n\n' +
      'This action cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    // Double confirmation
    const doubleConfirmed = confirm(
      'Final confirmation: Are you absolutely sure you want to delete your account?\n\n' +
      'All your data will be permanently deleted and cannot be recovered.'
    );

    if (!doubleConfirmed) {
      return;
    }

    setIsDeletingAccount(true);

    try {
      await apiClient.delete('/api/account');
      
      // Sign out the user
      await signOut();
      
      // Redirect to auth page
      navigate('/auth', { replace: true });
      
      // Show success message (will be shown on auth page)
      alert('Your account has been successfully deleted.');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete account';
      alert(`Error: ${errorMessage}`);
      setIsDeletingAccount(false);
    }
  };

  const activeAddresses = emailAddresses.filter(a => a.isActive);
  const inactiveAddresses = emailAddresses.filter(a => !a.isActive);

  return (
    <div className="page-shell">
      <div className="flex flex-col gap-3">
        <h1 className="text-3xl font-semibold text-ink-700">Settings</h1>
        <p className="text-sm text-ink-500 max-w-2xl">
          Tune what you share, confirm forwarding details, and keep your CrossCheckout account in sync with your inbox.
        </p>
      </div>

      <section className="card p-6">
        <h2 className="text-xl font-semibold text-ink-700">Privacy &amp; Data Sharing</h2>
        <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-2xl">
            <h3 className="text-lg font-medium text-ink-700">Anonymous contribution</h3>
            <p className="text-sm text-ink-500 mt-1">
              Help improve our community price index with anonymised line-item insights. Your personal details never leave your account.
            </p>
          </div>
          <label className="inline-flex items-center gap-3 rounded-full border border-ink-100/80 bg-white/70 px-4 py-2 shadow-sm">
            <input
              type="checkbox"
              checked={anonymousSharingEnabled}
              onChange={handleToggle}
              className="h-5 w-10 cursor-pointer rounded-full border-0 bg-ink-100 accent-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-ink-600">
              {anonymousSharingEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>
      </section>

      <section className="card p-6">
        <h2 className="text-xl font-semibold text-ink-700">Email Forwarding</h2>
        <p className="text-sm text-ink-500 mt-2 max-w-xl">
          Forward Sainsbury&apos;s receipt emails to one of your forwarding addresses below. We&apos;ll automatically parse and store them.
        </p>

        {isLoadingAddresses ? (
          <div className="mt-6 text-center text-sm text-ink-500">Loading addresses...</div>
        ) : (
          <>
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-ink-700">Active Forwarding Addresses</h3>
                <button
                  type="button"
                  onClick={handleCreateAddress}
                  disabled={isCreatingAddress || activeAddresses.length >= 5}
                  className="inline-flex items-center gap-2 rounded-xl border border-primary-600 bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingAddress ? 'Creating...' : 'Create New Address'}
                </button>
              </div>

              {activeAddresses.length === 0 ? (
                <div className="rounded-2xl border border-ink-100/80 bg-ink-50/70 p-5 text-center">
                  <p className="text-sm text-ink-500 mb-4">No active forwarding addresses</p>
                  <button
                    type="button"
                    onClick={handleCreateAddress}
                    disabled={isCreatingAddress}
                    className="inline-flex items-center gap-2 rounded-xl border border-primary-600 bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isCreatingAddress ? 'Creating...' : 'Create Your First Address'}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeAddresses.map((address) => (
                    <div
                      key={address.emailAddressId}
                      className="flex items-center justify-between rounded-xl border border-ink-100 bg-white p-4"
                    >
                      <div className="flex-1">
                        <code className="block font-mono text-sm text-ink-700">
                          {address.emailAddress}
                        </code>
                        <p className="text-xs text-ink-500 mt-1">
                          Created {new Date(address.createdAt * 1000).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeactivateAddress(address.emailAddressId)}
                        className="rounded-xl border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 transition hover:bg-ink-50"
                      >
                        Deactivate
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {activeAddresses.length >= 5 && (
                <p className="mt-4 text-xs text-ink-500">
                  Maximum of 5 active addresses reached. Deactivate an address to create a new one.
                </p>
              )}
            </div>

            {inactiveAddresses.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-medium text-ink-700 mb-4">Inactive Addresses</h3>
                <div className="space-y-3">
                  {inactiveAddresses.map((address) => (
                    <div
                      key={address.emailAddressId}
                      className="flex items-center justify-between rounded-xl border border-ink-100 bg-ink-50/50 p-4 opacity-75"
                    >
                      <div className="flex-1">
                        <code className="block font-mono text-sm text-ink-600">
                          {address.emailAddress}
                        </code>
                        <p className="text-xs text-ink-500 mt-1">
                          Deactivated {address.deactivatedAt ? new Date(address.deactivatedAt * 1000).toLocaleDateString() : 'recently'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteAddress(address.emailAddressId)}
                        className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-600 transition hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 rounded-2xl border border-ink-100/80 bg-ink-50/70 p-5">
              <ol className="list-decimal space-y-2 pl-5 text-sm text-ink-500">
                <li>Forward order confirmation emails from Sainsbury&apos;s to any active address above.</li>
                <li>We ingest, parse, and reconcile every receipt behind the scenes.</li>
                <li>Your shopping history appears automatically on the Orders and Analysis pages.</li>
              </ol>
            </div>
          </>
        )}
      </section>

      <section className="card p-6">
        <h2 className="text-xl font-semibold text-ink-700">Receipt Upload</h2>
        <p className="text-sm text-ink-500 mt-2 max-w-xl">
          Upload PDF receipts manually. You can select multiple files at once.
        </p>

        <div
          className={`mt-6 rounded-2xl border-2 border-dashed transition-colors ${
            isDragging
              ? 'border-primary-400 bg-primary-50/50'
              : 'border-ink-200 bg-ink-50/30'
          } p-8 text-center`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            multiple
            onChange={handleFileInputChange}
            className="hidden"
            id="pdf-upload"
          />
          <label htmlFor="pdf-upload" className="cursor-pointer">
            <svg
              className="mx-auto h-12 w-12 text-ink-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="mt-4">
              <p className="text-sm font-medium text-ink-700">
                {isDragging ? 'Drop PDF files here' : 'Drag and drop PDF files here'}
              </p>
              <p className="text-xs text-ink-500 mt-1">or</p>
              <button
                type="button"
                className="mt-2 inline-flex items-center gap-2 rounded-xl border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 shadow-sm transition hover:bg-ink-50"
                onClick={() => fileInputRef.current?.click()}
              >
                Browse files
              </button>
            </div>
          </label>
        </div>

        {selectedFiles.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-ink-700">
                Selected Files ({selectedFiles.length})
              </h3>
              <div className="flex gap-2">
                {selectedFiles.some(f => f.status === 'success' || f.status === 'error') && (
                  <button
                    type="button"
                    onClick={clearCompleted}
                    className="text-sm text-ink-500 hover:text-ink-700"
                  >
                    Clear completed
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={isUploading || !selectedFiles.some(f => f.status === 'pending')}
                  className="inline-flex items-center gap-2 rounded-xl border border-primary-600 bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    'Upload All'
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {selectedFiles.map((fileStatus, index) => (
                <div
                  key={`${fileStatus.file.name}-${index}`}
                  className="flex items-center gap-4 rounded-xl border border-ink-100 bg-white p-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink-700 truncate">
                      {fileStatus.file.name}
                    </p>
                    <p className="text-xs text-ink-500 mt-1">
                      {formatFileSize(fileStatus.file.size)}
                    </p>
                    {fileStatus.status === 'success' && fileStatus.receiptId && (
                      <p className="text-xs text-green-600 mt-1">
                        Receipt ID: {fileStatus.receiptId}
                      </p>
                    )}
                    {fileStatus.status === 'error' && fileStatus.error && (
                      <p className="text-xs text-red-600 mt-1">{fileStatus.error}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {fileStatus.status === 'pending' && (
                      <span className="text-xs text-ink-500">Ready to upload</span>
                    )}
                    {fileStatus.status === 'uploading' && (
                      <div className="flex items-center gap-2 text-xs text-primary-600">
                        <svg
                          className="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Uploading...
                      </div>
                    )}
                    {fileStatus.status === 'success' && (
                      <svg
                        className="h-5 w-5 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                    {fileStatus.status === 'error' && (
                      <svg
                        className="h-5 w-5 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    )}
                    {(fileStatus.status === 'pending' || fileStatus.status === 'error') && (
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-ink-400 hover:text-ink-600"
                        aria-label="Remove file"
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="card p-6">
        <h2 className="text-xl font-semibold text-ink-700">MCP Connectors</h2>
        <p className="text-sm text-ink-500 mt-2 max-w-xl">
          Connect AI agents to interrogate your receipts, price history, and frequently bought products via the Model Context Protocol.
        </p>

        {/* ---- MCP Endpoint (always visible) ---- */}
        {(() => {
          const mcpEndpoint = mcpConnectors[0]?.endpoint
            || newConnectorEndpoint
            || (typeof window !== 'undefined' ? `${window.location.origin}/api/mcp` : 'http://localhost:3002/mcp');
          return (
            <div className="mt-5 rounded-xl border border-ink-100 bg-ink-50/50 p-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-sm font-medium text-ink-700">MCP Server Endpoint</p>
                <button
                  type="button"
                  onClick={() => copyToClipboard(mcpEndpoint)}
                  className="rounded-lg border border-ink-200 bg-white px-3 py-1 text-xs font-medium text-ink-600 hover:bg-ink-50 transition"
                >
                  Copy URL
                </button>
              </div>
              <code className="block rounded-lg bg-white border border-ink-100 px-3 py-2 text-sm font-mono text-ink-700 break-all select-all">
                {mcpEndpoint}
              </code>
              <p className="text-xs text-ink-400 mt-2">
                Use this URL when configuring any MCP-compatible tool. You&apos;ll also need an access token from a connector below.
              </p>
            </div>
          );
        })()}

        {mcpLoading ? (
          <div className="mt-6 text-sm text-ink-500">Loading connectors...</div>
        ) : (
          <>
            <div className="mt-6 flex items-center justify-between">
              <h3 className="text-lg font-medium text-ink-700">Your Connectors</h3>
              {!showCreateForm && (
                <button
                  type="button"
                  onClick={() => { setShowCreateForm(true); setNewConnectorName(''); }}
                  className="inline-flex items-center gap-2 rounded-xl border border-primary-600 bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700"
                >
                  Add Connector
                </button>
              )}
            </div>

            {/* ---- Create form ---- */}
            {showCreateForm && (
              <div className="mt-4 rounded-xl border border-primary-200 bg-primary-50/40 p-4">
                <p className="text-sm font-medium text-ink-700 mb-3">New Connector</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newConnectorName}
                    onChange={(e) => setNewConnectorName(e.target.value)}
                    placeholder="Connector name (e.g. Cursor, ChatGPT, Claude Desktop)"
                    maxLength={100}
                    className="flex-1 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-700 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateMcpConnector();
                      if (e.key === 'Escape') setShowCreateForm(false);
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleCreateMcpConnector}
                    disabled={mcpCreating}
                    className="rounded-xl border border-primary-600 bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {mcpCreating ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="rounded-xl border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-600 hover:bg-ink-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* ---- New token banner ---- */}
            {newConnectorToken && (
              <div className="mt-4 rounded-xl border border-green-200 bg-green-50/50 p-4">
                <p className="text-sm font-medium text-green-800 mb-2">Connector created. Copy the token now &mdash; it won&apos;t be shown again:</p>
                <div className="flex gap-2">
                  <code className="flex-1 truncate rounded bg-white px-3 py-2 text-xs text-ink-700 border border-ink-100">
                    {newConnectorToken}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(newConnectorToken)}
                    className="rounded-xl border border-primary-600 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            {/* ---- Connector list ---- */}
            {mcpConnectors.length === 0 && !newConnectorToken && !showCreateForm ? (
              <div className="mt-4 rounded-2xl border border-ink-100/80 bg-ink-50/70 p-5 text-center">
                <p className="text-sm text-ink-500 mb-2">No connectors yet</p>
                <p className="text-xs text-ink-500 mb-4 max-w-md mx-auto">
                  Create a connector to get an access token. Each AI tool you connect should have its own connector so you can track usage and revoke access independently.
                </p>
                <button
                  type="button"
                  onClick={() => { setShowCreateForm(true); setNewConnectorName(''); }}
                  className="inline-flex gap-2 rounded-xl border border-primary-600 bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
                >
                  Create Connector
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {mcpConnectors.map((conn) => (
                  <div
                    key={conn.connectorId}
                    className="rounded-xl border border-ink-100 bg-white p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Name – inline editable */}
                        {renamingId === conn.connectorId ? (
                          <div className="flex items-center gap-2 mb-1">
                            <input
                              type="text"
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              maxLength={100}
                              className="rounded-lg border border-ink-200 bg-white px-2 py-1 text-sm text-ink-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRename(conn.connectorId);
                                if (e.key === 'Escape') setRenamingId(null);
                              }}
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => handleRename(conn.connectorId)}
                              className="text-xs text-primary-600 font-medium hover:underline"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setRenamingId(null)}
                              className="text-xs text-ink-500 hover:underline"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-ink-700">{conn.clientName}</p>
                            <button
                              type="button"
                              onClick={() => { setRenamingId(conn.connectorId); setRenameValue(conn.clientName); }}
                              className="text-ink-400 hover:text-ink-600"
                              title="Rename"
                              aria-label="Rename connector"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                          </div>
                        )}
                        <p className="text-xs text-ink-500 font-mono">{conn.clientId}</p>
                        <p className="text-xs text-ink-500 mt-1">
                          Created {new Date(conn.createdAt * 1000).toLocaleDateString()}
                          {conn.lastUsedAt ? (
                            <> &middot; Last used {new Date(conn.lastUsedAt * 1000).toLocaleDateString()}</>
                          ) : (
                            <> &middot; Never used</>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Link
                          to={`/settings/connectors/${conn.connectorId}/activity`}
                          className="rounded-xl border border-ink-200 bg-white px-3 py-2 text-xs font-medium text-ink-600 hover:bg-ink-50 transition"
                          title="View activity log"
                        >
                          Activity
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleResetKey(conn.connectorId)}
                          disabled={resettingKeyId === conn.connectorId}
                          className="rounded-xl border border-ink-200 bg-white px-3 py-2 text-xs font-medium text-ink-600 hover:bg-ink-50 transition disabled:opacity-50"
                          title="Generate a new access token"
                        >
                          {resettingKeyId === conn.connectorId ? 'Resetting...' : 'Reset Key'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRevokeMcpConnector(conn.connectorId)}
                          className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-100 transition"
                        >
                          Revoke
                        </button>
                      </div>
                    </div>

                    {/* Reset token banner for this connector */}
                    {resetToken[conn.connectorId] && (
                      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                        <p className="text-xs font-medium text-amber-800 mb-2">New access token &mdash; copy it now, it won&apos;t be shown again:</p>
                        <div className="flex gap-2">
                          <code className="flex-1 truncate rounded bg-white px-2 py-1.5 text-xs text-ink-700 border border-ink-100">
                            {resetToken[conn.connectorId]}
                          </code>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(resetToken[conn.connectorId])}
                            className="rounded-lg border border-primary-600 bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ---- Connect your tools ---- */}
            {(() => {
              const endpoint = mcpConnectors[0]?.endpoint
                || newConnectorEndpoint
                || (typeof window !== 'undefined' ? `${window.location.origin}/api/mcp` : 'http://localhost:3002/mcp');
              return (
                <div className="mt-6 rounded-2xl border border-ink-100/80 bg-ink-50/70 p-5">
                  <p className="text-sm font-medium text-ink-700 mb-4">Connect your tools</p>

                  <div className="space-y-4">
                    {/* ChatGPT */}
                    <div className="rounded-xl border border-ink-100 bg-white p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-ink-700">ChatGPT</p>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(endpoint)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-600 hover:bg-ink-50 transition"
                        >
                          Copy endpoint
                        </button>
                      </div>
                      <p className="text-xs text-ink-500 mb-2">
                        To connect ChatGPT: go to <strong>Settings</strong> &rarr; <strong>Apps</strong>, enable <strong>Developer mode</strong>, then add an app: enter an app name, paste the MCP endpoint URL (use Copy endpoint above), and select <strong>OAuth</strong> for authentication. ChatGPT will discover the OAuth endpoints and sign you in via your CrossCheckout account.
                      </p>
                      <p className="text-xs text-ink-400">
                        OAuth discovery uses <code className="bg-ink-100 px-1 rounded">/.well-known/oauth-protected-resource</code> and <code className="bg-ink-100 px-1 rounded">/.well-known/oauth-authorization-server</code> at the server origin.
                      </p>
                    </div>

                    {/* Cursor */}
                    <div className="rounded-xl border border-ink-100 bg-white p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-ink-700">Cursor</p>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(JSON.stringify({
                            mcpServers: {
                              crosscheckout: {
                                url: endpoint,
                                transport: 'streamable-http',
                              },
                            },
                          }, null, 2))}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-600 hover:bg-ink-50 transition"
                        >
                          Copy Config
                        </button>
                      </div>
                      <p className="text-xs text-ink-500 mb-2">
                        Add to your project&apos;s <code className="text-ink-600 bg-ink-100 px-1 rounded">.cursor/mcp.json</code>:
                      </p>
                      <pre className="rounded-lg bg-ink-50 border border-ink-100 p-3 text-xs font-mono text-ink-700 overflow-x-auto select-all">{`{
  "mcpServers": {
    "crosscheckout": {
      "url": "${endpoint}",
      "transport": "streamable-http"
    }
  }
}`}</pre>
                      <p className="text-xs text-ink-400 mt-2">
                        When Cursor connects, it will prompt for your Bearer token. Paste the access token from your connector above.
                      </p>
                    </div>

                    {/* Claude Desktop */}
                    <div className="rounded-xl border border-ink-100 bg-white p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-ink-700">Claude Desktop</p>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(JSON.stringify({
                            mcpServers: {
                              crosscheckout: {
                                type: 'streamable-http',
                                url: endpoint,
                              },
                            },
                          }, null, 2))}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-600 hover:bg-ink-50 transition"
                        >
                          Copy Config
                        </button>
                      </div>
                      <p className="text-xs text-ink-500 mb-2">
                        Add to <code className="text-ink-600 bg-ink-100 px-1 rounded">claude_desktop_config.json</code>:
                      </p>
                      <pre className="rounded-lg bg-ink-50 border border-ink-100 p-3 text-xs font-mono text-ink-700 overflow-x-auto select-all">{`{
  "mcpServers": {
    "crosscheckout": {
      "type": "streamable-http",
      "url": "${endpoint}"
    }
  }
}`}</pre>
                      <p className="text-xs text-ink-400 mt-2">
                        Claude Desktop will ask for a Bearer token on first connection. Use the access token from your connector.
                      </p>
                    </div>

                    {/* Generic / other tools */}
                    <div className="rounded-xl border border-ink-100 bg-white p-4">
                      <p className="text-sm font-semibold text-ink-700 mb-2">Other MCP-compatible tools</p>
                      <p className="text-xs text-ink-500">
                        Any tool that supports the MCP Streamable HTTP transport can connect. Use the endpoint URL above with Bearer token authentication. The server exposes tools for listing receipts, viewing order details, price history, frequently bought products, and shopping analysis.
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-ink-100">
                    <p className="text-xs text-ink-500">
                      <strong className="text-ink-600">Quick start:</strong> 1. Create a connector and copy its token. 2. Add the endpoint URL and token to your tool. 3. Start asking about your shopping data.
                    </p>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </section>

      <section className="card p-6">
        <h2 className="text-xl font-semibold text-ink-700">Account</h2>
        <p className="text-sm text-ink-500 mt-2 max-w-xl">
          Need a fresh start? Permanently delete your account and all associated receipt data.
        </p>
        <p className="text-xs text-ink-400 mt-3 max-w-xl">
          Note: Anonymous data (if you opted in to sharing) cannot be removed as it cannot be tied back to your account.
        </p>
        <button
          onClick={handleDeleteAccount}
          disabled={isDeletingAccount}
          className="mt-6 inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isDeletingAccount ? (
            <>
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Deleting account...
            </>
          ) : (
            'Delete account'
          )}
        </button>
      </section>
    </div>
  );
}
